import { useCreateEventdetector, useShapeFetch } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useGetFilters } from '@ais-ui/websockets';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import { DetectorType } from '../Mutations/detectorForm';
import {
  convertFormValuesToBackend,
  lineDetectorFormSchema,
  LineDetectorFormValues,
} from './lineForm';
import { LineDetectorDialog } from './LineDetectorDialog';

interface CreateDetectorDialogProps {
  detectorType: DetectorType;
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
}

export const CreateLineDetectorDialog = ({
  detectorType,
  showDialog,
  setShowDialog,
}: CreateDetectorDialogProps) => {
  const { messages } = useLocalization();
  const { mutate } = useCreateEventdetector();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const shapeFetch = useShapeFetch();
  const { toast } = useToast();
  const { data: filters } = useGetFilters();
  const form = useForm<LineDetectorFormValues>({
    resolver: zodResolver(lineDetectorFormSchema),
    defaultValues: {
      enabled: true,
      selectedGroups: [],
      severity: 'note',
      filterId: 'no-filter',
      maintainShiplist: false,
      acknowledgeRequired: false,
      timeBeforeActivation: undefined,
      timeBeforeDeactivation: undefined,
      timeBetweenTrigger: undefined,
      timeoutWhenActive: undefined,
      sendShipNotification: false,
      shipNotificationText: '',
      useVesselDimensions: false,
      navigationStatusTriggerThreshold: 0,
      navigationStatusIgnoreThreshold: 0,
      driftingSpeedOverGroundThreshold: 0,
      driftingAngleThreshold: 0,
      driftingTimeThreshold: 0,
      exitCheckThreshold: 0,
      exitCheckTriggerLost: false,
      deltaSpeedOverGroundThreshold: 0,
      emails: [],
      phones: [],
    },
  });

  const onCreate = useCallback(
    async (data: LineDetectorFormValues) => {
      if (!filters) {
        return;
      }
      let shape = null;
      if (data.shapeId) {
        shape = await shapeFetch(data.shapeId);
      }

      if (!shape) {
        return;
      }

      const filter = filters.find((filter) => filter.uuid === data.filterId);

      const backendValues = convertFormValuesToBackend(
        detectorType,
        data,
        shape,
        filter
      );

      setIsSubmitting(true);

      const callBacks = mutateCallbacks(
        toast,
        messages,
        'create',
        'detector',
        setIsSubmitting,
        setShowDialog
      );

      mutate(
        {
          eventdetectorDefinition: backendValues,
          dc: 1707829314034,
        },
        callBacks
      );
    },
    [filters, shapeFetch, mutate, detectorType]
  );

  return (
    <LineDetectorDialog
      form={form}
      onSubmit={onCreate}
      setShowOuterDialog={setShowDialog}
      mutateType="create"
      isSubmitting={isSubmitting}
    ></LineDetectorDialog>
  );
};
